/**
 * positionApi.js — Phase 86.
 *
 * The ONE client for the canonical financial position. Every card and chart on
 * /accounts reads from here so nothing can drift apart again.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

async function authHeaders() {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    // Session can still be restoring on a cold load — one short retry beats a
    // silent 401 that would blank the whole page.
    await new Promise((r) => setTimeout(r, 700));
    ({ data: { session } } = await supabase.auth.getSession());
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// The last failure, kept so the UI can say WHAT went wrong instead of only that
// something did. "Error 401" and "timed out after 25s" need different fixes, and
// a user reporting a problem shouldn't have to open devtools to tell us which.
let lastError = null;

/** A function, not an exported binding — read at render time, always current. */
export function getLastPositionError() {
  return lastError;
}

async function get(path, { retry = true } = {}) {
  try {
    const res = await axios.get(`${API}${path}`, {
      headers: await authHeaders(), timeout: 25000,
    });
    lastError = null;
    return res.data;
  } catch (e) {
    if (retry) {
      await new Promise((r) => setTimeout(r, 900));
      return get(path, { retry: false });
    }
    const status = e?.response?.status || null;
    const detail = e?.response?.data?.detail || e?.response?.data?.error
      || (e?.code === 'ECONNABORTED' ? 'timed out' : e?.message) || 'unknown error';
    lastError = { path, status, detail: String(detail).slice(0, 160) };
    console.warn('position GET failed:', path, status, detail);
    return null;
  }
}

/** Canonical assets / liabilities / net worth + full resource disclosure. */
export function fetchPosition({ fresh = false } = {}) {
  // Balances are written straight to Supabase, so a refetch after a local change
  // must tell the server not to answer from its short-lived memo.
  return get(`/api/position${fresh ? '?fresh=1' : ''}`);
}

/** Recorded position history. Never reconstructed — may legitimately be empty. */
export function fetchPositionHistory() {
  return get('/api/position/history');
}

export const FRESHNESS_TONE = {
  fresh:                  { fg: '#3D7A63', label: 'Up to date' },
  aging:                  { fg: '#B07210', label: 'Ageing' },
  stale_vs_transactions:  { fg: '#B07210', label: 'May be out of date' },
  never_confirmed:        { fg: '#8A8A8A', label: 'Not confirmed' },
};
